import axios from "axios";
import { GENERIC_ERROR_MESSAGE } from "src/utils/errorMessages";
import { getLogger } from "src/utils/logger";

const logger = getLogger("promo.service");

const API_BASE_URL = process.env.REACT_APP_API_SERVER_URL || "http://localhost:6060";

/** Every reason the backend can give for refusing a claim. */
const CLAIM_REJECTIONS = [
  "already_claimed",
  "active_subscription",
  "trial_active",
  "not_eligible",
  "promo_ended",
] as const;

export type PromoClaimRejection = (typeof CLAIM_REJECTIONS)[number];

/**
 * Status codes the backend uses for each refusal, so an older or truncated body
 * without a `reason` still lands on the right screen instead of the generic
 * error. The `reason` string wins whenever it is present.
 */
const REJECTION_BY_STATUS: Record<number, PromoClaimRejection> = {
  403: "not_eligible",
  409: "already_claimed",
  410: "promo_ended",
};

/** The `subscription` object the claim writes, echoed back on success. */
export interface PromoSubscription {
  tier?: string;
  status?: string;
  trial_end_utc?: string;
  set_by?: string;
  updated_utc?: string;
}

export interface PromoClaimSuccess {
  outcome: "success";
  message: string;
  /** When the granted week runs out, ISO-8601 UTC. Null if the API omits it. */
  promoEndUtc: string | null;
  subscription: PromoSubscription | null;
}

export interface PromoClaimRejected {
  outcome: PromoClaimRejection;
  message: string;
}

/** Network failure, timeout, or any status with no reason we recognise. */
export interface PromoClaimError {
  outcome: "error";
  message: string;
}

export type PromoClaimResult = PromoClaimSuccess | PromoClaimRejected | PromoClaimError;

const asRecord = (data: unknown): Record<string, unknown> => {
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (parseError) {
      logger.error("Could not parse the claim response body", parseError);
      return {};
    }
  }

  return data && typeof data === "object" ? (data as Record<string, unknown>) : {};
};

const readString = (body: Record<string, unknown>, key: string): string | null => {
  const value = body[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
};

const readRejection = (body: Record<string, unknown>): PromoClaimRejection | null => {
  const reason = readString(body, "reason");
  return CLAIM_REJECTIONS.find((candidate) => candidate === reason) ?? null;
};

const toFailure = (status: number, data: unknown): PromoClaimResult => {
  const body = asRecord(data);
  // Backend routes are split between `message` and `error` for the human
  // readable reason, so read both before falling back to the generic string.
  const message = readString(body, "message") ?? readString(body, "error") ?? GENERIC_ERROR_MESSAGE;
  const rejection = readRejection(body) ?? REJECTION_BY_STATUS[status] ?? null;

  if (rejection) {
    return { outcome: rejection, message };
  }

  return { outcome: "error", message };
};

export const promoService = {
  /**
   * Claim the two free weeks of full access.
   *
   * Returns a result the page can render directly rather than throwing: every
   * refusal is an expected outcome with its own screen, not an error. This calls
   * axios directly instead of `callExternalApi` because that helper reduces a
   * failure to a single message, dropping both the status code and the `reason`
   * string this flow is built on.
   */
  claimFreeWeek: async (accessToken: string): Promise<PromoClaimResult> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/promo/claim`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        // The claim reads and writes Auth0 metadata and may check Stripe, so
        // allow for a cold start — but never hang the button indefinitely.
        timeout: 20000,
        // Resolve on every status so the refusal codes reach the mapping below
        // as data instead of arriving as thrown axios errors.
        validateStatus: () => true,
      });

      if (response.status < 200 || response.status >= 300) {
        logger.info("Promo claim refused with status %s", response.status);
        return toFailure(response.status, response.data);
      }

      const body = asRecord(response.data);
      const subscription = (body.subscription as PromoSubscription | undefined) ?? null;

      return {
        outcome: "success",
        message: readString(body, "message") ?? "Your free access is ready.",
        promoEndUtc: readString(body, "promo_end_utc") ?? subscription?.trial_end_utc ?? null,
        subscription,
      };
    } catch (error) {
      logger.error("Promo claim request failed", error);
      return { outcome: "error", message: GENERIC_ERROR_MESSAGE };
    }
  },
};
